/*
    Frequently used mixin for components which display lists of things.

    This component works off a bit of "magic" explained here:

        1. 'source' & 'getter' - are props which components should define representing
            the two-level Vuex state to an array for the Listable component to display.

        2. Listable's watch: component section is something TO PAY ATTENTION TO!
           It has a TON OF MAGIC and triggers a bunch of essential behavior automatically!

            - 'queryParams' holds an object holding query parameters
            that'll wind up being used in the backend to filter results from.
            (note that this is a 'data: var instead of a 'prop:')

            - 'search' can be used to do a term to do simple text matching
            (see https://www.django-rest-framework.org/api-guide/filtering/#searchfilter
             and grep for 'search_fields' in the backend/Django code)

            - 'page' & 'pageSize' are integers that refer to
             https://www.django-rest-framework.org/api-guide/pagination/#pagenumberpagination
             and give you flexibility in the number of elements you might want to
             show at a time in a given component.

            - ANY mutations to 'page', 'truePage', 'search' wind up re-querying
            the API automatically and making http requests with the updated filtering.

        3. 'mode' is either "loadMore" or "pagination", and used to denote if
           the mixed in component operates infinite-scroll-like ("loadMore")
           OR if it shows the entire list ("pagination").

    Q. How does the infinite scroll stuff work?
    A.
        1. arbitrary components mixin Listable
        2. external components can pass a callable v-ontrigger loadMore as needed
        3. loadMore() increments 'truePage'
        4. watcher on 'truePage' winds up calling fetchData() after step 1
        5. fetchData() makes an API http request by dispatching a Redux store action!

    Q. fetchRoute()?
    A. components mixing in Listable can implement this method and
       returning a string denoting the Vuex action that will trigger the API
       http call to gather its list data from.

    - Warning:  You may want to AVOID mutating 'this.queryParams' and 'this.search'
                with this.$set(...) as it may have side-effects you aren't expecting.
                (see Listable's watch: section below!)

    - The guts of Listable is also in the history of src/components/GenericList.vue
      Because it was its original form & name!  See its beginning in: 6bb1900cf

    Also see: src/_utilities/Scrollable.js
 */
import axios from 'axios';

export const PAGINATION_MODE_LOAD_MORE = 'loadMore';
export const PAGINATION_MODE_REGULAR = 'pagination';

export default {
  name: 'GenericList',
  props: {
    mode: {
      type: String,
      required: false,
      default: 'pagination',
      validator: (value) => ['pagination', 'loadMore'].includes(value),
    },
    page: {
      type: Number,
      required: false,
      default: 1,
    },
    pageSize: {
      type: Number,
      required: false,
      default: 10,
    },
    showSearch: {
      type: Boolean,
      required: false,
      default: false,
    },
    source: {
      type: String,
      required: true,
    },
    getter: {
      type: String,
      required: false,
      default: 'items',
    },
    noDataLabel: {
      type: String,
      required: false,
      default: 'No data!',
    },
    loadMoreLabel: {
      type: String,
      required: false,
      default: 'Load more',
    },
    reloadLabel: {
      type: String,
      required: false,
      default: 'Reload',
    },
    searchLabel: {
      type: String,
      required: false,
      default: 'Search',
    },
    loadingLabel: {
      type: String,
      required: false,
      default: 'Loading',
    },
    errorLabel: {
      type: String,
      required: false,
      default: 'There was an error loading data.',
    },
    tryAgainLabel: {
      type: String,
      required: false,
      default: 'Try again',
    },
    customReset: {
      type: String,
      default: '',
    },
    /** Newer 'fetchData' calls will cancel previous pending axios requests */
    noMultiRequest: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    // tip: can override in mounted()!  use ...spread operator to keep defaults!
    return {
      hasdata: false,
      nextPageExists: true,
      requestFailed: false,

      truePage: -1,
      rawRoute: '',

      search: '',
      queryParams: {},

      // Only if noMultiRequest = true
      lastRequestTokenSource: null,
      numResults: 0,
    };
  },
  mounted() {
    /**
     * This is a generic list mixin. This is used by base-list component
     * but can be called by any component
     * Use these to follow its parent
     * console.log('mounted listable', this.$options.name);
    */
    this.reset();
    const splittedRoute = this.$route.path.split('/page');

    // eslint-disable-next-line
    this.rawRoute = splittedRoute[0];
    this.truePage = this.page;
    // this.search = '';
  },
  computed: {
    storeState() {
      const storeState = this.$store.state;

      if (storeState[this.source] === undefined) {
        // console.error(`Resource: ${this.source} does not exist in the store.`);
        return null;
      }
      return storeState;
    },
    elements() {
      return this.storeState[this.source][this.getter];
    },
    fetchRoute() {
      if (this.mode === 'loadMore') {
        return `${this.source}/addPage`;
      }
      return `${this.source}/fetchAll`;
    },
    paginationRoute() {
      return `${this.rawRoute}${this.rawRoute.endsWith('/') ? '' : '/'}page`.substring(1);
    },
    loading() {
      return this.storeState[this.source].isLoading;
    },
  },
  methods: {
    reset() {
      if (this.customReset !== '') {
        this.$store.dispatch(`${this.source}/${this.customReset}`);
      } else {
        this.$store.dispatch(`${this.source}/reset`);
      }
    },
    reload() {
      this.fetchData();
    },
    loadMore() {
      // console.log('LOAD MORE!');
      if (this.loading) {
        // console.log('Already loading! Abort');
        return;
      }

      if (this.nextPageExists) {
        // console.log('Next page exists. Loading more...');
        this.truePage += 1;
      } else {
        // console.log(`nextPageExists: ${this.nextPageExists} was falsy.  truePage: ${this.truePage}`);
      }
    },
    fetchData() {
      if (this.loading) {
        // console.log('already loading');
        return;
      }

      const kwargs = {
        ...this.queryParams,
        page: this.truePage,
        search: this.search,
        page_size: this.pageSize,
      };

      if (this.noMultiRequest) {
        if (this.lastRequestTokenSource) {
          // console.log('Cancelling ongoing request');
          this.lastRequestTokenSource.cancel(`Existing request for source '${this.source}' was overriden`);
        }
        this.lastRequestTokenSource = axios.CancelToken.source();
        kwargs.cancelToken = this.lastRequestTokenSource.token;
      }

      this.requestFailed = false;

      this.$store.dispatch(this.fetchRoute, kwargs).then((response) => {
        if (response.data.count) {
          this.numResults = response.data.count;
        }
        this.lastRequestTokenSource = null;
        if (response && response.data) {
          this.nextPageExists = response.data.next != null;
        }
      }).catch((response) => {
        if (axios.isCancel(response)) {
          // console.log('listable.js request was CANCELLED', kwargs);
        } else {
          this.lastRequestTokenSource = null;
        }
        if (response.response === undefined) {
          // Network error
          this.requestFailed = true;
        } else if (response.response.status === 404) {
          this.$router.push({ name: 'ErrorPage' });
        } else {
          this.requestFailed = true; // 500 error
        }
      }).finally(() => {
        /** I commented this since the module is taking care of removing the loading status */
        // this.$store.commit(`${this.source}/setIsLoading`, false);
      });
    },
  },
  watch: {
    page(newval) {
      this.truePage = newval;
    },
    truePage() {
      this.fetchData();
    },
    search() {
      this.reset();
      if (this.truePage === 1) this.fetchData();
      else this.truePage = 1;
    },
    queryParams() {
      this.reset();
      if (this.truePage === 1) this.fetchData();
      else this.truePage = 1;
    },
  },
};
